import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { ArrowLeft, Palette, ShieldCheck } from "lucide-react";
import AuroraBrandHeader from "./AuroraBrandHeader";
import { formatLocalizedDate, journeyMessage, overlayClassName } from "../../lib/dashboardAppearance";

export default function DashboardWelcomeHero({
  greeting,
  date = new Date(),
  locale,
  visual,
  onOpenAppearance,
  showcaseMode = false,
}) {
  const onPhoto = Boolean(visual?.src);
  const overlay = overlayClassName(visual?.overlay);
  const dateLabel = formatLocalizedDate(date, locale);
  const message = journeyMessage(visual?.period);

  return (
    <section
      className="relative overflow-hidden rounded-2xl min-h-[250px] sm:min-h-[280px] lg:min-h-[clamp(280px,34vh,330px)] xl:min-h-[clamp(300px,34vh,360px)] border border-slate-200"
      data-testid="dashboard-visual-hero"
    >
      {onPhoto ? (
        <img
          src={visual.src}
          alt=""
          className="absolute inset-0 h-full w-full object-cover object-center motion-reduce:transition-none"
          aria-hidden="true"
        />
      ) : (
        <div
          className="absolute inset-0 bg-gradient-to-br from-sky-50 via-slate-100 to-slate-200"
          data-testid="dashboard-visual-no-photo"
          aria-hidden="true"
        />
      )}
      <div
        className={`absolute inset-0 bg-gradient-to-t ${onPhoto ? overlay : "from-white/40 via-white/20 to-transparent"}`}
        aria-hidden="true"
      />
      <div className="relative z-10 flex h-full flex-col justify-between gap-6 p-4 sm:p-6 lg:p-8">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <AuroraBrandHeader onPhoto={onPhoto} />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={`min-h-11 ${onPhoto ? "border-white/40 bg-black/25 text-white hover:bg-black/40 hover:text-white" : "border-slate-200 bg-white text-slate-800"}`}
              onClick={onOpenAppearance}
              data-testid="dashboard-visual-appearance"
            >
              <Palette className="mr-2 h-4 w-4" />
              Apariencia
            </Button>
            <Button
              asChild
              variant="outline"
              size="sm"
              className={`min-h-11 ${onPhoto ? "border-white/40 bg-black/25 text-white hover:bg-black/40 hover:text-white" : "border-slate-200 bg-white text-slate-800"}`}
            >
              <Link to={showcaseMode ? "/" : "/dashboard"} data-testid="dashboard-visual-back">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {showcaseMode ? "Volver al inicio" : "Volver al panel actual"}
              </Link>
            </Button>
          </div>
        </div>
        <div className={`max-w-2xl rounded-xl px-1 sm:px-0 ${onPhoto ? "text-white" : "text-slate-900"}`}>
          <p className="text-2xl sm:text-3xl font-semibold tracking-tight drop-shadow-sm" data-testid="dashboard-visual-greeting">
            {greeting}
          </p>
          <p className={`mt-2 text-sm sm:text-base ${onPhoto ? "text-white/90" : "text-slate-600"}`} data-testid="dashboard-visual-date">
            {dateLabel}
          </p>
          <p className={`mt-2 text-sm sm:text-base ${onPhoto ? "text-white/90" : "text-slate-600"}`}>
            {message}
          </p>
          <p className={`mt-4 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs sm:text-sm ${onPhoto ? "bg-black/35 text-white" : "bg-white/90 text-slate-700 border border-slate-200"}`}>
            <ShieldCheck className="h-4 w-4 shrink-0" aria-hidden="true" />
            Tu información clínica está protegida
          </p>
        </div>
      </div>
    </section>
  );
}
